import { AlertTriangleIcon, ArrowLeftIcon, Loader2Icon, ReceiptIcon } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { toast } from 'sonner'

import {
  titulosCpRevendaApi,
  type Baixa,
  type CreditosDaRevenda,
  type ListaDeTitulos,
  type MemoriaDeCalculoDoTitulo,
  type PagamentoAvulso,
  type TituloDaLista,
  type TituloResumo,
} from '../../services/titulosCpRevendaApi'
import { getErrorMessage } from '../../utils'
import { formatCurrency, formatDate, rotuloDaCompetencia } from '../../utils/format'
import {
  FORMAS_DE_BAIXA,
  rotuloDaForma,
  rotuloDaOrigemDaBaixa,
  rotuloDaOrigemDaObrigacao,
  rotuloDaOrigemDoCredito,
  rotuloDoEstado,
  seloDoTitulo,
  valorParaApi,
} from '../../utils/tituloEstado'

/** `cp` registra baixa e pagamento avulso; `rs` só lê (a API recusa o resto com 403). */
export type ModoDosTitulos = 'cp' | 'rs'

const Selo = ({ t }: { t: { estado: string; vencido: boolean } }) => {
  const s = seloDoTitulo(t)
  return (
    <span
      data-testid="selo-do-titulo"
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${s.cor}`}
    >
      {s.rotulo}
    </span>
  )
}

const Carregando = ({ texto }: { texto: string }) => (
  <div className="flex items-center justify-center py-12">
    <Loader2Icon className="h-8 w-8 animate-spin text-blue-600" />
    <span className="ml-2 text-gray-600">{texto}</span>
  </div>
)

const Erro = ({ titulo, mensagem }: { titulo: string; mensagem: string }) => (
  <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-6">
    <div className="flex items-center space-x-3">
      <AlertTriangleIcon className="h-6 w-6 text-red-600" />
      <div>
        <h3 className="font-medium text-red-900">{titulo}</h3>
        <p className="mt-1 text-red-700">{mensagem}</p>
      </div>
    </div>
  </div>
)

const Campo = ({ rotulo, children }: { rotulo: string; children: ReactNode }) => (
  <label className="block text-sm">
    <span className="mb-1 block font-medium text-gray-700">{rotulo}</span>
    {children}
  </label>
)

const INPUT = 'w-full rounded-md border border-gray-300 px-3 py-2 text-sm'

const data = (v: string | null) => (v ? formatDate(v, false) : '—')

// ---------------------------------------------------------------------------------------------
// Crédito da revenda com o CP
// ---------------------------------------------------------------------------------------------

export function CreditoDaRevenda({
  revendaId,
  recarregar = 0,
}: {
  revendaId: string
  /** Muda quando um pagamento avulso pode ter gerado crédito. */
  recarregar?: number
}) {
  const [dados, setDados] = useState<CreditosDaRevenda | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    let cancelado = false
    setDados(null)
    setErro(null)
    titulosCpRevendaApi
      .creditos(revendaId)
      .then((d) => !cancelado && setDados(d))
      .catch((e) => !cancelado && setErro(getErrorMessage(e)))
    return () => {
      cancelado = true
    }
  }, [revendaId, recarregar])

  if (erro) return <Erro titulo="Erro ao carregar o crédito" mensagem={erro} />
  if (!dados) return <Carregando texto="Carregando crédito..." />
  return (
    <section data-testid="credito-da-revenda" className="rounded-lg bg-white p-4 shadow">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-900">Crédito com o provedor</h3>
        <span data-testid="saldo-de-credito" className="text-xl font-bold text-gray-900">
          {formatCurrency(dados.saldo)}
        </span>
      </div>
      {dados.lancamentos.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">Nenhum lançamento de crédito.</p>
      ) : (
        <ul className="mt-3 divide-y divide-gray-100 text-sm">
          {dados.lancamentos.map((c) => (
            <li key={c.id} className="flex justify-between gap-3 py-2">
              <span className="text-gray-700">
                {rotuloDaOrigemDoCredito(c.origem)}
                <span className="ml-2 text-xs text-gray-500">{formatDate(c.created_at)}</span>
              </span>
              <span className={c.valor < 0 ? 'text-red-700' : 'text-green-700'}>
                {formatCurrency(c.valor)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------------------------
// Modais de baixa e de pagamento avulso (mesma trava de duplo clique: A10)
// ---------------------------------------------------------------------------------------------

type CamposDePagamento = {
  valor: string
  data: string
  forma: string
  referencia: string
  descricao: string
}

const agoraLocal = () => {
  const d = new Date()
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset())
  return d.toISOString().slice(0, 16)
}

function FormularioDePagamento({
  titulo,
  confirmar,
  onClose,
  extra,
  valorSugerido,
  onSubmit,
}: {
  titulo: string
  confirmar: string
  onClose: () => void
  extra?: ReactNode
  /** Saldo que a API devolveu: só preenche o campo, o usuário confere. */
  valorSugerido?: number
  onSubmit: (c: {
    amount: string
    payment_date: string
    payment_method: string
    reference: string | null
    description: string | null
  }) => Promise<void>
}) {
  const [c, setC] = useState<CamposDePagamento>({
    valor: '',
    data: agoraLocal(),
    forma: 'pix_external',
    referencia: '',
    descricao: '',
  })
  const [enviando, setEnviando] = useState(false)
  const trava = useRef(false)
  const [erro, setErro] = useState<string | null>(null)

  const enviar = async (e: FormEvent) => {
    e.preventDefault()
    if (trava.current) return // duplo clique: o estado do React só desabilita no próximo render
    const amount = valorParaApi(c.valor)
    if (!amount) return setErro('Informe um valor maior que zero, com até duas casas decimais.')
    if (!c.data) return setErro('Informe a data do pagamento.')
    trava.current = true
    setEnviando(true)
    setErro(null)
    try {
      await onSubmit({
        amount,
        payment_date: new Date(c.data).toISOString(),
        payment_method: c.forma,
        reference: c.referencia.trim() || null,
        description: c.descricao.trim() || null,
      })
    } catch (err) {
      const msg = getErrorMessage(err)
      setErro(msg)
      toast.error(msg)
    } finally {
      trava.current = false
      setEnviando(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <form
        role="dialog"
        aria-label={titulo}
        onSubmit={enviar}
        className="max-h-[90vh] w-full space-y-3 overflow-y-auto rounded-t-lg bg-white p-5 shadow-xl sm:max-w-md sm:rounded-lg"
      >
        <h3 className="text-lg font-semibold text-gray-900">{titulo}</h3>
        {extra}
        <Campo rotulo="Valor (R$)">
          <input
            className={INPUT}
            inputMode="decimal"
            value={c.valor}
            onChange={(e) => setC({ ...c, valor: e.target.value })}
            aria-label="Valor"
          />
        </Campo>
        {valorSugerido !== undefined && valorSugerido > 0 && (
          <button
            type="button"
            className="text-xs text-blue-700 underline"
            onClick={() => setC({ ...c, valor: valorSugerido.toFixed(2).replace('.', ',') })}
          >
            Usar o saldo ({formatCurrency(valorSugerido)})
          </button>
        )}
        <Campo rotulo="Data do pagamento">
          <input
            type="datetime-local"
            className={INPUT}
            value={c.data}
            onChange={(e) => setC({ ...c, data: e.target.value })}
            aria-label="Data do pagamento"
          />
        </Campo>
        <Campo rotulo="Forma">
          <select
            className={INPUT}
            value={c.forma}
            onChange={(e) => setC({ ...c, forma: e.target.value })}
            aria-label="Forma"
          >
            {FORMAS_DE_BAIXA.map((f) => (
              <option key={f.valor} value={f.valor}>
                {f.rotulo}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Referência (comprovante)">
          <input
            className={INPUT}
            value={c.referencia}
            maxLength={255}
            onChange={(e) => setC({ ...c, referencia: e.target.value })}
            aria-label="Referência"
          />
        </Campo>
        <Campo rotulo="Observação">
          <input
            className={INPUT}
            value={c.descricao}
            maxLength={500}
            onChange={(e) => setC({ ...c, descricao: e.target.value })}
            aria-label="Observação"
          />
        </Campo>
        {erro && (
          <p role="alert" className="text-sm text-red-700">
            {erro}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={enviando}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={enviando}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {enviando ? 'Registrando...' : confirmar}
          </button>
        </div>
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Detalhe
// ---------------------------------------------------------------------------------------------

export function MemoriaDeCalculo({ calculo }: { calculo: MemoriaDeCalculoDoTitulo }) {
  const vazio = calculo.grupos.length === 0 && calculo.desconhecidas.quantidade === 0
  return (
    <section data-testid="memoria-de-calculo" className="rounded-lg bg-white p-4 shadow">
      <h3 className="text-base font-semibold text-gray-900">Memória de cálculo</h3>
      {vazio ? (
        <p className="mt-2 text-sm text-gray-500">
          Este título não tem obrigações ligadas (título anterior ao razão).
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          {calculo.grupos.map((g) => (
            <div key={g.origem} data-testid={`grupo-${g.origem}`}>
              <div className="flex justify-between text-sm font-medium text-gray-900">
                <span>
                  {rotuloDaOrigemDaObrigacao(g.origem)}{' '}
                  <span className="font-normal text-gray-500">({g.quantidade})</span>
                </span>
                <span>{formatCurrency(g.subtotal)}</span>
              </div>
              <ul className="mt-1 divide-y divide-gray-100 text-xs text-gray-600">
                {g.obrigacoes.map((o) => (
                  <li key={o.id} className="flex justify-between gap-3 py-1">
                    <span className="truncate">
                      {rotuloDaCompetencia(o.competencia)} · {o.chave_origem}
                    </span>
                    <span>{o.valor === null ? '—' : formatCurrency(o.valor)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {calculo.desconhecidas.quantidade > 0 && (
            <div data-testid="grupo-desconhecidas" className="rounded-md bg-amber-50 p-3 text-sm">
              <p className="font-medium text-amber-900">
                {calculo.desconhecidas.quantidade} obrigação(ões) de valor desconhecido
              </p>
              <p className="text-xs text-amber-800">Fora do total até o preço ser conferido.</p>
            </div>
          )}
          <div className="flex justify-between border-t pt-2 text-sm font-semibold">
            <span>Total conhecido</span>
            <span>{formatCurrency(calculo.total_conhecido)}</span>
          </div>
        </div>
      )}
    </section>
  )
}

function BaixasDoTitulo({ baixas }: { baixas: Baixa[] }) {
  return (
    <section data-testid="baixas-do-titulo" className="rounded-lg bg-white p-4 shadow">
      <h3 className="text-base font-semibold text-gray-900">Baixas</h3>
      {baixas.length === 0 ? (
        <p className="mt-2 text-sm text-gray-500">Nenhuma baixa registrada.</p>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">
          {baixas.map((b) => (
            <li key={b.id} className="space-y-0.5 py-2" data-testid="baixa">
              <div className="flex justify-between gap-3 font-medium text-gray-900">
                <span>{formatCurrency(b.amount)}</span>
                <span>{formatDate(b.payment_date)}</span>
              </div>
              <div className="text-xs text-gray-600">
                {rotuloDaForma(b.payment_method)} · {rotuloDaOrigemDaBaixa(b.origem)}
                {b.reference ? ` · Ref. ${b.reference}` : ''}
              </div>
              {b.description && <div className="text-xs text-gray-500">{b.description}</div>}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

export function TituloDetalhe({
  modo,
  tituloId,
  onVoltar,
}: {
  modo: ModoDosTitulos
  tituloId: string
  onVoltar: () => void
}) {
  const [resumo, setResumo] = useState<TituloResumo | null>(null)
  const [baixas, setBaixas] = useState<Baixa[]>([])
  const [calculo, setCalculo] = useState<MemoriaDeCalculoDoTitulo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [baixando, setBaixando] = useState(false)

  const carregar = useCallback(async () => {
    // Tudo da API, em conjunto: depois de uma baixa o estado e o saldo são relidos, nunca somados aqui.
    const [r, b, c] = await Promise.all([
      titulosCpRevendaApi.resumo(tituloId),
      titulosCpRevendaApi.baixas(tituloId),
      titulosCpRevendaApi.calculo(tituloId),
    ])
    setResumo(r)
    setBaixas(b)
    setCalculo(c)
  }, [tituloId])

  useEffect(() => {
    let cancelado = false
    setErro(null)
    setResumo(null)
    carregar().catch((e) => !cancelado && setErro(getErrorMessage(e)))
    return () => {
      cancelado = true
    }
  }, [carregar])

  const podeBaixar =
    modo === 'cp' && resumo && resumo.saldo > 0 && !['quitado', 'cancelado'].includes(resumo.estado)

  return (
    <div className="space-y-4">
      <button
        onClick={onVoltar}
        className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeftIcon className="mr-1 h-4 w-4" /> Voltar para títulos
      </button>
      {erro && <Erro titulo="Erro ao carregar o título" mensagem={erro} />}
      {!erro && !resumo && <Carregando texto="Carregando título..." />}
      {resumo && (
        <>
          <section className="rounded-lg bg-white p-4 shadow">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-gray-900">Título</h2>
                <Selo t={resumo} />
              </div>
              {podeBaixar && (
                <button
                  onClick={() => setBaixando(true)}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white"
                >
                  Registrar baixa
                </button>
              )}
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              {(
                [
                  ['Total', resumo.total, 'total'],
                  ['Pago', resumo.pago, 'pago'],
                  ['Saldo', resumo.saldo, 'saldo'],
                  ['Crédito usado', resumo.credito_usado, 'credito-usado'],
                ] as const
              ).map(([rotulo, valor, id]) => (
                <div key={id}>
                  <dt className="text-gray-500">{rotulo}</dt>
                  <dd data-testid={`titulo-${id}`} className="font-semibold text-gray-900">
                    {formatCurrency(valor)}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
          {calculo && <MemoriaDeCalculo calculo={calculo} />}
          <BaixasDoTitulo baixas={baixas} />
        </>
      )}
      {baixando && resumo && (
        <FormularioDePagamento
          titulo="Registrar baixa"
          confirmar="Registrar baixa"
          valorSugerido={resumo.saldo}
          onClose={() => setBaixando(false)}
          onSubmit={async (corpo) => {
            await titulosCpRevendaApi.registrarBaixa(tituloId, corpo)
            toast.success('Baixa registrada')
            setBaixando(false)
            // a baixa JÁ foi feita: falha ao reler é erro da tela, não do formulário (senão convida a repetir)
            await carregar().catch((e) => setErro(getErrorMessage(e)))
          }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------------------------

const TAMANHO_DA_PAGINA = 50
const ESTADOS_DO_FILTRO = ['pendente', 'parcial', 'quitado', 'cancelado', 'vencido']

export function TitulosLista({
  modo,
  revendaPropriaId,
  onAbrir,
}: {
  modo: ModoDosTitulos
  /** RS: a org ativa (o crédito é dela). CP: não se aplica. */
  revendaPropriaId?: string | null
  onAbrir: (id: string) => void
}) {
  const [revendaId, setRevendaId] = useState('')
  const [competencia, setCompetencia] = useState('')
  const [estado, setEstado] = useState('')
  const [lista, setLista] = useState<ListaDeTitulos | null>(null)
  const [revendas, setRevendas] = useState<{ id: string; nome: string }[]>([])
  const [pagina, setPagina] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [avulso, setAvulso] = useState(false)
  const [resultado, setResultado] = useState<PagamentoAvulso | null>(null)
  const [tick, setTick] = useState(0)

  // As revendas do seletor (filtro e pagamento avulso) vêm de fonte própria, a mesma régua do pagamento
  // avulso no backend: independem dos títulos listados e do filtro de competência.
  useEffect(() => {
    if (modo !== 'cp') return
    let cancelado = false
    titulosCpRevendaApi
      .revendas()
      .then((r) => !cancelado && setRevendas(r))
      .catch((e) => !cancelado && toast.error(getErrorMessage(e)))
    return () => {
      cancelado = true
    }
  }, [modo])

  useEffect(() => {
    setPagina(0)
  }, [revendaId, competencia, estado])

  useEffect(() => {
    let cancelado = false
    setErro(null)
    titulosCpRevendaApi
      .listar({
        revenda_id: revendaId || undefined,
        competencia: competencia || undefined,
        estado: estado || undefined,
        skip: pagina * TAMANHO_DA_PAGINA,
        limit: TAMANHO_DA_PAGINA,
      })
      .then((l) => !cancelado && setLista(l))
      .catch((e) => !cancelado && setErro(getErrorMessage(e)))
    return () => {
      cancelado = true
    }
  }, [revendaId, competencia, estado, pagina, tick])

  const creditoDe = modo === 'rs' ? revendaPropriaId : revendaId || null

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {modo === 'cp' && (
          <Campo rotulo="Revenda">
            <select
              className={INPUT}
              value={revendaId}
              onChange={(e) => setRevendaId(e.target.value)}
              aria-label="Revenda"
            >
              <option value="">Todas</option>
              {revendas.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.nome}
                </option>
              ))}
            </select>
          </Campo>
        )}
        <Campo rotulo="Competência">
          <input
            type="month"
            className={INPUT}
            value={competencia}
            onChange={(e) => setCompetencia(e.target.value)}
            aria-label="Competência"
          />
        </Campo>
        <Campo rotulo="Estado">
          <select
            className={INPUT}
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            aria-label="Estado"
          >
            <option value="">Todos</option>
            {ESTADOS_DO_FILTRO.map((e) => (
              <option key={e} value={e}>
                {e === 'vencido' ? 'Vencido' : rotuloDoEstado(e)}
              </option>
            ))}
          </select>
        </Campo>
        {modo === 'cp' && (
          <button
            onClick={() => setAvulso(true)}
            className="ml-auto rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white"
          >
            Registrar pagamento avulso
          </button>
        )}
      </div>

      {resultado && (
        <section
          data-testid="resultado-do-avulso"
          className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm"
        >
          <p className="font-medium text-green-900">
            Pagamento de {formatCurrency(resultado.valor)} registrado.
          </p>
          <ul className="mt-1 text-green-900">
            {resultado.alocacoes.map((a) => (
              <li key={a.id}>
                {formatCurrency(a.valor)} abateu o título (saldo era {formatCurrency(a.saldo_antes)}
                , vencimento {data(a.vencimento)})
              </li>
            ))}
            {resultado.credito && (
              <li data-testid="sobra-de-credito">
                Sobrou {formatCurrency(resultado.credito.valor)} como crédito da revenda.
              </li>
            )}
          </ul>
          <button className="mt-2 text-xs underline" onClick={() => setResultado(null)}>
            Fechar
          </button>
        </section>
      )}

      {erro ? (
        <Erro titulo="Erro ao carregar títulos" mensagem={erro} />
      ) : !lista ? (
        <Carregando texto="Carregando títulos..." />
      ) : lista.titulos.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-12 text-center">
          <ReceiptIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum título</h3>
          <p className="text-gray-600">Os títulos aparecem aqui após o fechamento de cada mês.</p>
        </div>
      ) : (
        <>
          <ListaDeLinhas titulos={lista.titulos} modo={modo} onAbrir={onAbrir} />
          {lista.total > TAMANHO_DA_PAGINA && (
            <nav
              aria-label="Paginação"
              data-testid="paginacao"
              className="flex items-center justify-between text-sm"
            >
              <button
                disabled={pagina === 0}
                onClick={() => setPagina((p) => p - 1)}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                Anterior
              </button>
              <span>
                {lista.skip + 1}–{lista.skip + lista.titulos.length} de {lista.total}
              </span>
              <button
                disabled={lista.skip + lista.titulos.length >= lista.total}
                onClick={() => setPagina((p) => p + 1)}
                className="rounded-md border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                Próxima
              </button>
            </nav>
          )}
        </>
      )}

      {creditoDe && <CreditoDaRevenda revendaId={creditoDe} recarregar={tick} />}

      {avulso && (
        <PagamentoAvulsoModal
          revendas={revendas}
          inicial={revendaId}
          onClose={() => setAvulso(false)}
          onFeito={(r) => {
            setAvulso(false)
            setResultado(r)
            setTick((n) => n + 1)
          }}
        />
      )}
    </div>
  )
}

function ListaDeLinhas({
  titulos,
  modo,
  onAbrir,
}: {
  titulos: TituloDaLista[]
  modo: ModoDosTitulos
  onAbrir: (id: string) => void
}) {
  const contraparte = (t: TituloDaLista) => (modo === 'cp' ? t.revenda_nome : t.cp_nome) || '—'
  return (
    <div className="rounded-lg bg-white shadow" data-testid="lista-de-titulos">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3">Competência</th>
              <th className="px-4 py-3">{modo === 'cp' ? 'Revenda' : 'Provedor'}</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3 text-right">Pago</th>
              <th className="px-4 py-3 text-right">Saldo</th>
              <th className="px-4 py-3">Vencimento</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 text-sm">
            {titulos.map((t) => (
              <tr
                key={t.id}
                data-testid="linha-do-titulo"
                onClick={() => onAbrir(t.id)}
                className="cursor-pointer hover:bg-gray-50"
              >
                <td className="px-4 py-3 font-medium">{rotuloDaCompetencia(t.competencia)}</td>
                <td className="px-4 py-3">{contraparte(t)}</td>
                <td className="px-4 py-3">
                  <Selo t={t} />
                </td>
                <td className="px-4 py-3 text-right">{formatCurrency(t.total)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(t.pago)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatCurrency(t.saldo)}</td>
                <td className="px-4 py-3">{data(t.vencimento)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="divide-y divide-gray-200 md:hidden">
        {titulos.map((t) => (
          <button
            key={t.id}
            data-testid="linha-do-titulo"
            onClick={() => onAbrir(t.id)}
            className="block w-full space-y-1 p-4 text-left"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">
                {rotuloDaCompetencia(t.competencia)} · {contraparte(t)}
              </span>
              <Selo t={t} />
            </div>
            <div className="text-xs text-gray-500">
              Total {formatCurrency(t.total)} · Pago {formatCurrency(t.pago)}
            </div>
            <div className="text-sm font-semibold">
              Saldo {formatCurrency(t.saldo)} · vence {data(t.vencimento)}
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function PagamentoAvulsoModal({
  revendas,
  inicial,
  onClose,
  onFeito,
}: {
  revendas: { id: string; nome: string }[]
  inicial: string
  onClose: () => void
  onFeito: (r: PagamentoAvulso) => void
}) {
  const [revenda, setRevenda] = useState(inicial || revendas[0]?.id || '')
  return (
    <FormularioDePagamento
      titulo="Registrar pagamento avulso"
      confirmar="Registrar pagamento"
      onClose={onClose}
      extra={
        <Campo rotulo="Revenda">
          <select
            className={INPUT}
            value={revenda}
            onChange={(e) => setRevenda(e.target.value)}
            aria-label="Revenda do pagamento"
          >
            {revendas.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome}
              </option>
            ))}
          </select>
        </Campo>
      }
      onSubmit={async (corpo) => {
        if (!revenda) throw new Error('Escolha a revenda do pagamento.')
        const r = await titulosCpRevendaApi.registrarPagamentoAvulso({
          ...corpo,
          revenda_id: revenda,
        })
        toast.success('Pagamento registrado')
        onFeito(r)
      }}
    />
  )
}
